import React from 'react';
import { useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { ChannelMessageAttachmentType } from 'shared/types/graphql';
import { FIND_UNIQUE_ATTACHMENT } from './graphql/findUniqueChannelMessageAttachment';
import { Flex, Box, Text, Image } from '@televet/kibble-ui';
import InvoiceAttachment from 'pages/Conversations/components/ChannelMessage/MessageBubble/Attachments/Invoice';
import useClinicUser from 'shared/hooks/useClinicUser';

export const ChannelMessageAttachment = (): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { channelMessageAttachmentId } = useParams<{ channelMessageAttachmentId: string }>();
  const [getAttachment, { data, loading }] = useLazyQuery<{
    findUniqueChannelMessageAttachment: {
      id: string;
      channelMessage: { id: string }[];
      url: string;
      filename: string;
      attachmentType: ChannelMessageAttachmentType;
      entityId: string;
    };
  }>(FIND_UNIQUE_ATTACHMENT, {
    variables: {
      id: channelMessageAttachmentId,
    },
  });
  useEffect(() => {
    if (channelMessageAttachmentId && currentClinicId) {
      getAttachment();
    }
  }, [channelMessageAttachmentId, currentClinicId, getAttachment]);
  const { id, url, filename, attachmentType, entityId, channelMessage } =
    data?.findUniqueChannelMessageAttachment || {};
  const [channelMessageObject] = channelMessage || [];
  const { id: channelMessageId } = channelMessageObject || {};

  if (loading) {
    return <LoadingSpinner />;
  }
  return (
    <Flex justifyContent="center" alignItems="center" flex="1" height="100%">
      {(url && filename) || attachmentType === ChannelMessageAttachmentType.Invoice ? (
        <>
          {attachmentType === ChannelMessageAttachmentType.Image && (
            <Image
              maxHeight="90%"
              maxWidth="90%"
              height="auto"
              width="auto"
              draggable={true}
              src={url}
              alt={filename || 'File name could not be found'}
            />
          )}
          {attachmentType === ChannelMessageAttachmentType.Video && (
            <Box as="video" maxWidth="90%" maxHeight="90%" width="auto" height="auto" src={url} controls>
              Your browser does not support the video tag.
            </Box>
          )}
          {attachmentType === ChannelMessageAttachmentType.File && (
            <Box
              as="iframe"
              width="100%"
              height="100%"
              allow="fullscreen"
              title={filename || attachmentType}
              src={url}
            />
          )}
          {attachmentType === ChannelMessageAttachmentType.Invoice && (
            <Flex display="column" justifyContent="center" alignItems="center">
              <InvoiceAttachment
                attachment={{
                  id: id || '',
                  attachmentType,
                  entityId: entityId || '',
                }}
                channelId={channelMessageId || ''}
                setCurrentInvoiceId={(): void => {
                  // Invoice Modal not implemented in this view
                }}
              />
              <Box textAlign="center" mt="20px">
                <Text variant="interactive">
                  <a href={`/conversations/${channelMessageId}`}>Go to conversation</a>
                </Text>
              </Box>
            </Flex>
          )}
        </>
      ) : (
        <>No attachment found </>
      )}
    </Flex>
  );
};
